/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useRef } from '@web-stories-wp/react';
import {
  generatePatternStyles,
  convertToCSS,
  hasGradient,
} from '@web-stories-wp/patterns';
/**
 * Internal dependencies
 */
import { useTransformHandler } from '../../components/transform';
import { getOverlayFilterStyle } from '../../utils/overlayStack';

function getTargetDimensions(target) {
  const { offsetHeight, offsetWidth } = target;
  const rect =
    (!offsetHeight || !offsetWidth) && target.getBoundingClientRect?.();
  return {
    height: offsetHeight || rect?.height,
    width: offsetWidth || rect?.width,
  };
}

function getPaintBounds(pattern, target, paintBounds, useOverlayFilterStyle) {
  // Solid and legacy CSS paint do not consume dimensions. Reading them during
  // every colour movement can force layout after the previous style write.
  const needsBounds =
    (hasGradient(pattern) && pattern.geometryVersion === 2) ||
    (useOverlayFilterStyle &&
      pattern?.type === 'linear' &&
      pattern.start &&
      pattern.end &&
      Array.isArray(pattern.stops));
  return needsBounds ? paintBounds || getTargetDimensions(target) : undefined;
}

function useColorTransformHandler({
  id,
  targetRef,
  expectedStyle,
  expectedFilterId,
  fallbackColor,
  fallbackBlur = 0,
  paintBounds,
  resetOnNullTransform = true,
  useOverlayFilterStyle = false,
}) {
  const fallbackColorRef = useRef(fallbackColor);
  const fallbackBlurRef = useRef(fallbackBlur);
  const paintBoundsRef = useRef(paintBounds);
  fallbackColorRef.current = fallbackColor;
  fallbackBlurRef.current = fallbackBlur;
  paintBoundsRef.current = paintBounds;

  useTransformHandler(id, (transform) => {
    const target =
      undefined !== targetRef?.current ? targetRef.current : targetRef;
    if (target) {
      if (transform === null) {
        if (resetOnNullTransform) {
          target.style.cssText = '';
        }
      } else {
        const { color, overlayFilterId, resetOverlayFilterPreview, style } =
          transform;
        if (expectedFilterId && overlayFilterId !== expectedFilterId) {
          return;
        }
        if (resetOverlayFilterPreview) {
          const currentFallbackColor = fallbackColorRef.current;
          if (currentFallbackColor && style === 'background') {
            const bounds = getPaintBounds(
              currentFallbackColor,
              target,
              paintBoundsRef.current,
              useOverlayFilterStyle
            );
            const patternStyle = useOverlayFilterStyle
              ? getOverlayFilterStyle(
                  {
                    pattern: currentFallbackColor,
                    blur: fallbackBlurRef.current,
                  },
                  bounds
                )
              : generatePatternStyles(
                  currentFallbackColor,
                  style,
                  null,
                  bounds
                );
            target.style.cssText = convertToCSS(patternStyle);
          }
          return;
        }
        // If the transforming style and the expected style don't match, return.
        if (expectedStyle && expectedStyle !== style) {
          return;
        }
        if (color && style) {
          if (style === 'color' && hasGradient(color)) {
            return;
          }
          // In case we're changing text color, we need the children instead of the element itself.
          if ('color' === style && target.children?.length > 0) {
            const patternStyle = generatePatternStyles(color, style);

            for (const node of target.children) {
              let existingStyles = {};

              for (const key of Object.keys(node.style)) {
                const isValidKey = Number.isNaN(Number(key));
                const value = node.style[key];

                if (isValidKey && value) {
                  existingStyles[key] = value;
                }
              }
              const toApply = convertToCSS({
                ...existingStyles,
                ...patternStyle,
              });

              node.style.cssText = toApply;
            }
          } else {
            const bounds = getPaintBounds(
              color,
              target,
              paintBoundsRef.current,
              useOverlayFilterStyle
            );
            const patternStyle =
              useOverlayFilterStyle && style === 'background'
                ? getOverlayFilterStyle(
                    { pattern: color, blur: fallbackBlurRef.current },
                    bounds
                  )
                : generatePatternStyles(color, style, null, bounds);

            target.style.cssText = convertToCSS(patternStyle);
          }
        }
      }
    }
  });
}

export default useColorTransformHandler;
